<template>
  <div class="organisation-details" v-if="model" :class="{ updating: saveInProgress }">
    <OrganisationMemberMoveDialog ref="memberMoveDialog" />
    <OrganisationGroupAddDialog ref="groupAddDialog" />
    <div class="row">
      <div class="col-sm-12">
        <h1>
          <span v-if="!isRenaming"
            >{{ model.name }}
            <a @click="startRenaming" href="javascript:void(0)" aria-label="rename"
              ><small><span class="text-sm glyphicon glyphicon-pencil"></span></small></a
          ></span>
          <div
            class="renaming-container"
            v-if="isRenaming"
            :class="{ 'has-error': v$.newName.$error }"
          >
            <input
              type="text"
              v-model="newName"
              @blur="v$.newName.$touch()"
              maxlength="40"
              aria-label="new name"
            />
            <div class="renaming-buttons">
              <button
                class="btn btn-primary"
                @click="saveRenaming"
                :disabled="v$.$error"
                type="button"
              >
                Save
              </button>
              <button class="btn btn-default" @click="cancelRenaming" type="button">Cancel</button>
            </div>
          </div>
        </h1>
        <p>
          <i>created on {{ dateCreated }}</i>
        </p>
      </div>
    </div>
    <div class="row nav-row">
      <div class="col-sm-8">
        <ul class="nav nav-pills tab-list" role="tablist">
          <li role="presentation" :class="{ active: currentTab === 'members' }">
            <a href="#" role="tab" data-test="members-tab" @click.prevent="selectMembersTab"
              >Members ({{ model.members.length }})</a
            >
          </li>
          <li role="presentation" :class="{ active: currentTab === 'teams' }">
            <a href="#" role="tab" data-test="teams-tab" @click.prevent="currentTab = 'teams'"
              >Teams ({{ model.teams.length }})</a
            >
          </li>
          <li role="presentation" :class="{ active: currentTab === 'groups' }">
            <a href="#" role="tab" data-test="org-groups-tab" @click.prevent="currentTab = 'groups'"
              >Groups ({{ model.groups.length }})</a
            >
          </li>
          <li role="presentation" :class="{ active: currentTab === 'data' }">
            <a href="#" role="tab" data-test="org-data-tab" @click.prevent="currentTab = 'data'"
              >Data</a
            >
          </li>
          <li role="presentation" :class="{ active: currentTab === 'settings' }">
            <a
              href="#"
              role="tab"
              data-test="org-settings-tab"
              @click.prevent="currentTab = 'settings'"
              >Settings</a
            >
          </li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-8 content-column">
        <!-- Tab panes -->

        <MembersTab
          v-show="currentTab === 'members'"
          :model="model"
          @revalidate-org-data="revalidateData"
        />

        <TeamsTab
          v-show="currentTab === 'teams'"
          :id="id"
          :model="model"
          :teamsOrGroups="TeamsOrGroups"
          :canCreateGroupsAndTeams="true"
          :sortedTeams="sortedTeams"
          @add-groups="addGroups"
        />

        <GroupsTab
          v-show="currentTab === 'groups'"
          :id="id"
          :model="model"
          :teamsOrGroups="TeamsOrGroups"
          :canCreateGroupsAndTeams="true"
          :sortedGroups="sortedGroups"
          @add-groups="addGroups"
        />

        <DataTab
          v-show="currentTab === 'data'"
          :model="model"
          @revalidate-org-data="revalidateData"
        />

        <SettingsTab
          v-if="currentTab === 'settings' && !!frameworkModel"
          :frameworkModel="frameworkModel"
          :settingsUpdateIsLoading="settingsUpdateIsLoading"
          :settingsUpdatedSuccess="settingsUpdatedSuccess ?? undefined"
          @save-settings="saveSettings"
        />
      </div>
    </div>
  </div>

  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import axios from "axios";
import dayjs from "dayjs";
import { debug as d } from "debug";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import TeamsOrGroups from "../../typings/enums/teams-or-groups";
import { setTitle } from "../../services/utilities/set-title";
import {
  CustomFrameworkForOrganisationsApiModel,
  GroupAndMembersApiModel,
  OrganisationAndMembersApiModel,
  OrganisationDescriptionApiModel,
  OrganisationMemberApiModel,
  OrganisationMemberApiRole,
  OrganisationMemberUpdateApiModel,
} from "../../api-models";
import OrganisationMemberMoveDialog from "../../components/admin/organisation/organisation-member-move-dialog.vue";
import OrganisationGroupAddDialog from "../../components/admin/organisation/organisation-group-add-dialog.vue";
import SettingsTab from "../../components/admin/organisation/settings-tab.vue";
import DataTab from "../../components/admin/organisation/data-tab.vue";
import GroupsTab from "../../components/admin/organisation/groups-tab.vue";
import TeamsTab from "../../components/admin/organisation/teams-tab.vue";
import MembersTab from "../../components/admin/organisation/members-tab.vue";
import LoadingPage from "../loading-page.vue";
import organisationNameRegex from "../../services/utilities/organisation-name-regex";

const debug = d("cs.OrganisationPage");

function sortGroups(groups: GroupAndMembersApiModel[]): GroupAndMembersApiModel[] {
  return groups.slice().sort((a, b) => {
    if (a.group.name < b.group.name) {
      return -1;
    }
    if (a.group.name > b.group.name) {
      return 1;
    }

    return 0;
  });
}

const props = defineProps<{
  id: string;
}>();

const model = ref<OrganisationAndMembersApiModel | null>(null);
const frameworkModel = ref<CustomFrameworkForOrganisationsApiModel | null>(null);
const visibleOrganisations = ref<OrganisationDescriptionApiModel[]>([]);
const isRenaming = ref(false);
const newName = ref("");
const saveInProgress = ref(false);
const appliedFilter = ref<string | null>(null);
const settingsUpdateIsLoading = ref(false);
const settingsUpdatedSuccess = ref<boolean | null>(null);
const memberMoveDialog = ref();
const groupAddDialog = ref();

const rules = {
  newName: {
    required,
    maxLength: maxLength(40),
    regex: (value) => organisationNameRegex.test(value),
  },
};

const state = {
  newName,
};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const dateCreated = computed(() => {
  return model.value ? dayjs(model.value.dateCreated).format("DD/MM/YYYY") : null;
});

const sortedTeams = computed(() => {
  return model.value ? sortGroups(model.value.teams) : [];
});

const sortedGroups = computed(() => {
  return model.value ? sortGroups(model.value.groups) : [];
});

async function loadModel() {
  debug("loadModel started");
  saveInProgress.value = true;
  visibleOrganisations.value = (await axios.get("/api/organisation")).data;
  model.value = (await axios.get(`/api/organisation/${props.id}`)).data;
  saveInProgress.value = false;
  setTitle(`Organisation: ${props.id}`);
  debug("loadModel finished");
}

async function loadCustomFrameworkSettings() {
  frameworkModel.value = (await axios.get(`/api/organisationframework/${props.id}`)).data;
}

onMounted(async () => {
  debug("Mounted");
  try {
    await loadModel();
    await loadCustomFrameworkSettings();
  } catch (e) {
    debug(e);
  }
});

async function saveSettings(updatedFrameworkModel: CustomFrameworkForOrganisationsApiModel) {
  try {
    settingsUpdateIsLoading.value = true;
    await axios.post(`/api/organisationframework/${props.id}`, updatedFrameworkModel, {
      headers: { "content-type": "application/json" },
    });

    settingsUpdatedSuccess.value = true;
  } catch (e) {
    debug(e);
    settingsUpdatedSuccess.value = false;
  } finally {
    settingsUpdateIsLoading.value = false;
  }
}

async function revalidateData() {
  try {
    await loadModel();
    await loadCustomFrameworkSettings();
  } catch (e) {
    debug(e);
  }
}

function startRenaming() {
  isRenaming.value = true;
  newName.value = model.value?.name || "";
}

function cancelRenaming() {
  isRenaming.value = false;
  saveInProgress.value = false;
}

function saveRenaming(): void {
  if (v$.value.$touch) {
    (v$.value.$touch as any)();
  } else {
    throw new Error("Vuelidate this.$v.$touch() unavailable");
  }

  if (v$.value.$invalid) {
    return;
  }

  saveInProgress.value = true;
  axios
    .post(`/api/organisation/${props.id}`, JSON.stringify(newName.value), {
      headers: { "content-type": "application/json" },
    })
    .then(() => {
      isRenaming.value = false;
      saveInProgress.value = false;
      loadModel();
    })
    .catch(() => {
      // eslint-disable-next-line no-alert
      alert("There was a problem renaming the organisation, please try again.");
      cancelRenaming();
    });
}

function changeRole(member: OrganisationMemberApiModel, newRole: OrganisationMemberApiRole) {
  if (member.role === newRole) {
    return;
  }

  saveInProgress.value = true;
  axios
    .post(
      "/api/organisationmember/",
      {
        userId: member.shortUserDetails.id,
        organisationId: props.id,
        role: newRole,
      } as OrganisationMemberUpdateApiModel,
      {
        headers: { "content-type": "application/json" },
      },
    )
    .then((resp) => {
      saveInProgress.value = false;
      member.role = newRole;
    })
    .catch((error) => {
      alert("There was a problem changing the role, please try again.");
      saveInProgress.value = false;
    });
}

function addGroups(teamsOrGroups: TeamsOrGroups) {
  (groupAddDialog.value as any)
    .open(model.value as OrganisationAndMembersApiModel, teamsOrGroups)
    .then((result) => {
      if (result) {
        loadModel();
      }
    })
    .finally(() => {
      (groupAddDialog.value as any)?.close();
    });
}

// TAB SYSTEM
const currentTab = ref("members");

function selectMembersTab() {
  appliedFilter.value = null;
  currentTab.value = "members";
}
</script>

<style scoped lang="less">
.organisation-details {
  margin: 0 auto;
  width: 95%;
}

.organisation-details.updating {
  opacity: 0.5;
  cursor: wait;

  & > * {
    user-select: none;
    pointer-events: none;
  }
}

.renaming-container {
  input {
    max-width: 580px;
    width: 580px;
  }
}

.renaming-buttons {
  display: inline-block;
  margin-left: 10px;

  button {
    margin-left: 10px;
  }
}

.tab-list {
  padding: 1rem 0;
  border-bottom: 1px solid lightgray;
}

.tab-pane table td {
  line-height: 2;
}

.dropdown-menu {
  // Position to the left to ensure visibility on mobile
  right: 0;
  left: unset;
}
</style>
